import type { Continuity } from '@dissidium/contracts';
import { add, hash, integer, UNITS_PER_METRE } from '../kernel/primitives.ts';
import { isWalkable, metricDistance, validateNavigationMap, type NavigationMap, type NavPosition } from '../navigation/index.ts';
import { changeRemains, validateLedger } from '../continuity/ledger.ts';

/**
 * D02.2 — corpse-work stations at the Quiet Kilns (SYS10).
 * Encodes docs/world/08-magic.md "A station's limits" and the seven hard
 * limits: a station binds at most three bodies within 30 metres of its
 * anchor; breaking or releasing the anchor ends the imposed motion; nothing
 * here revives a person. Working dead are remains records, never people.
 */

export const STATION_COUNT = 3;
export const MAX_BODIES_PER_STATION = 3;
export const TETHER_RANGE_UNITS = 30 * UNITS_PER_METRE;
export const ANCHOR_ACCESS_RANGE_UNITS = 2 * UNITS_PER_METRE;
/** The slice's fixed opening: 2/2/3 active bodies plus two incomplete, nine total. */
export const OPENING_ACTIVE_PER_STATION = [2, 2, 3] as const;
export const OPENING_INCOMPLETE_BODIES = 2;
export const TOTAL_KILN_BODIES = 9;

export type WorkPattern = Readonly<{
  id: string;
  label: string;
  /** One narrow repeated bodily action: a short fixed loop of positions. */
  steps: readonly NavPosition[];
}>;

export type BodyStatus = 'active' | 'stilled';
export type AnchorIntegrity = 'intact' | 'broken' | 'released';

export type StationBody = {
  remainsId: string;
  position: NavPosition;
  workPatternId: string;
  patternStep: number;
  status: BodyStatus;
};

/** A body in storage, not yet bound to a station; completion is an authored escalation. */
export type StoredBody = { remainsId: string; position: NavPosition };

export type CorpseStation = {
  id: string;
  anchor: NavPosition;
  integrity: AnchorIntegrity;
  bodies: StationBody[];
};

export type RemainsEvent =
  | Readonly<{ type: 'pattern-step'; tick: number; stationId: string; remainsId: string; position: NavPosition }>
  | Readonly<{ type: 'tether-broken'; tick: number; stationId: string; remainsId: string; reason: 'threshold-crossed' | 'out-of-range' | 'anchor-broken' | 'anchor-released' }>;

/** A Narrow Threshold boundary: interrupts a prepared corpse tether crossing it. Single use; ordinary actors can also damage the line. */
export type PreparedThreshold = Readonly<{
  id: string;
  riteId: 'narrow-threshold';
  cells: readonly NavPosition[];
  spent: boolean;
}>;

export type RemainsOperation = {
  tick: number;
  zone: 'Z04';
  stations: CorpseStation[];
  storage: StoredBody[];
  events: RemainsEvent[];
};

export type EvidenceFact = Readonly<{ id: string; kind: string; finding: string; source: string }>;

const compareText = (a: string, b: string): number => a < b ? -1 : a > b ? 1 : 0;
const sameCell = (a: NavPosition, b: NavPosition): boolean => a.x === b.x && a.y === b.y && a.layer === b.layer;

function withinTether(anchor: NavPosition, position: NavPosition): boolean {
  const distance = metricDistance(anchor, position);
  return distance !== null && distance <= TETHER_RANGE_UNITS;
}

function assertWalkable(map: NavigationMap, position: NavPosition, label: string): void {
  if (!isWalkable(map, position)) throw new Error(`${label} is outside authored walkability`);
}

function assertRemains(continuity: Continuity, remainsId: string, disposition: 'animated' | 'exposed'): void {
  const remains = continuity.remains[remainsId];
  if (!remains) throw new Error(`${remainsId} is not a recorded body; corpse-work cannot invent remains`);
  if (remains.location !== 'Z04') throw new Error(`${remainsId} lies outside the Quiet Kilns`);
  if (remains.disposition !== disposition) throw new Error(`${remainsId} is ${remains.disposition}; this station needs an ${disposition} body`);
  if (continuity.persons[remains.personId]?.lifecycle !== 'dead') throw new Error('Corpse-work binds remains of the permanently dead only');
}

export function createOperation(
  continuity: Continuity,
  map: NavigationMap,
  patterns: readonly WorkPattern[],
  stations: readonly { id: string; anchor: NavPosition; bodies: readonly { remainsId: string; position: NavPosition; workPatternId: string }[] }[],
  storage: readonly StoredBody[],
): RemainsOperation {
  validateLedger(continuity);
  validateNavigationMap(map);
  if (stations.length !== STATION_COUNT) throw new Error(`The kiln operation runs exactly ${STATION_COUNT} stations`);
  const seen = new Set<string>();
  const patternIds = new Map(patterns.map(pattern => [pattern.id, pattern]));
  for (const pattern of patterns) {
    if (integer(pattern.steps.length) < 1) throw new Error(`Work pattern ${pattern.id} needs at least one step`);
  }
  const built: CorpseStation[] = stations.map(spec => {
    if (!spec.id || seen.has(`station:${spec.id}`)) throw new Error('Station IDs must be non-empty and unique');
    seen.add(`station:${spec.id}`);
    assertWalkable(map, spec.anchor, `Anchor of ${spec.id}`);
    if (integer(spec.bodies.length) > MAX_BODIES_PER_STATION) throw new Error(`${spec.id} binds at most ${MAX_BODIES_PER_STATION} bodies`);
    const bodies = [...spec.bodies].sort((a, b) => compareText(a.remainsId, b.remainsId)).map(body => {
      if (seen.has(body.remainsId)) throw new Error(`${body.remainsId} cannot serve two stations`);
      seen.add(body.remainsId);
      assertRemains(continuity, body.remainsId, 'animated');
      assertWalkable(map, body.position, body.remainsId);
      if (!withinTether(spec.anchor, body.position)) throw new Error(`${body.remainsId} lies beyond the 30-metre tether of ${spec.id}`);
      const pattern = patternIds.get(body.workPatternId);
      if (!pattern) throw new Error(`Unknown work pattern ${body.workPatternId}`);
      for (const step of pattern.steps) {
        assertWalkable(map, step, `Pattern ${pattern.id}`);
        if (!withinTether(spec.anchor, step)) throw new Error(`Pattern ${pattern.id} would drag ${body.remainsId} beyond 30 metres of its anchor`);
      }
      return { remainsId: body.remainsId, position: structuredClone(body.position), workPatternId: pattern.id, patternStep: 0, status: 'active' as const };
    });
    return { id: spec.id, anchor: structuredClone(spec.anchor), integrity: 'intact' as const, bodies };
  });
  const stored = [...storage].sort((a, b) => compareText(a.remainsId, b.remainsId)).map(body => {
    if (seen.has(body.remainsId)) throw new Error(`${body.remainsId} is already accounted for`);
    seen.add(body.remainsId);
    assertRemains(continuity, body.remainsId, 'exposed');
    assertWalkable(map, body.position, body.remainsId);
    return structuredClone(body);
  });
  return { tick: 0, zone: 'Z04', stations: built, storage: stored, events: [] };
}

/** The bible's fixed slice opening: 2/2/3 active across three stations plus two incomplete in storage. */
export function assertQuietKilnsOpening(operation: RemainsOperation): void {
  const active = operation.stations.map(station => station.bodies.length);
  if (active.join('/') !== OPENING_ACTIVE_PER_STATION.join('/')) throw new Error(`Opening distribution is ${active.join('/')}, not 2/2/3`);
  if (operation.storage.length !== OPENING_INCOMPLETE_BODIES) throw new Error(`Opening storage holds ${operation.storage.length}, not two incomplete bodies`);
  if (add(active.reduce(add, 0), operation.storage.length) !== TOTAL_KILN_BODIES) throw new Error('Nine burial bodies must be accounted for');
}

export const operationHash = (operation: RemainsOperation): string => hash(operation);

function stillBody(operation: RemainsOperation, station: CorpseStation, body: StationBody, reason: Extract<RemainsEvent, { type: 'tether-broken' }>['reason']): void {
  if (body.status !== 'active') return;
  body.status = 'stilled';
  operation.events.push({ type: 'tether-broken', tick: operation.tick, stationId: station.id, remainsId: body.remainsId, reason });
}

/** One fixed simulation tick of imposed work. Pausing is the host's concern: it simply does not call this. */
export function stepOperation(
  input: RemainsOperation,
  patterns: readonly WorkPattern[],
  thresholds: readonly PreparedThreshold[] = [],
): { operation: RemainsOperation; spentThresholdIds: string[] } {
  const operation = structuredClone(input);
  operation.tick = add(operation.tick, 1);
  const spentThresholdIds: string[] = [];
  const patternIds = new Map(patterns.map(pattern => [pattern.id, pattern]));
  for (const station of operation.stations) {
    if (station.integrity !== 'intact') continue;
    for (const body of station.bodies) {
      if (body.status !== 'active') continue;
      const pattern = patternIds.get(body.workPatternId);
      if (!pattern) throw new Error(`Unknown work pattern ${body.workPatternId}`);
      const nextStep = pattern.steps[body.patternStep % pattern.steps.length]!;
      const liveThreshold = thresholds.find(threshold => !threshold.spent && threshold.cells.some(cell => sameCell(cell, nextStep)));
      if (liveThreshold) {
        // A prepared boundary interrupts the correspondence; the body never crosses, and the medium is spent.
        stillBody(operation, station, body, 'threshold-crossed');
        if (!spentThresholdIds.includes(liveThreshold.id)) spentThresholdIds.push(liveThreshold.id);
        continue;
      }
      if (!withinTether(station.anchor, nextStep)) {
        stillBody(operation, station, body, 'out-of-range');
        continue;
      }
      body.position = structuredClone(nextStep);
      body.patternStep = add(body.patternStep, 1);
      operation.events.push({ type: 'pattern-step', tick: operation.tick, stationId: station.id, remainsId: body.remainsId, position: body.position });
    }
  }
  return { operation, spentThresholdIds };
}

/** Crossing a prepared threshold spends its medium; damaging the line spends it too. Ordinary people cross freely. */
export function spendThreshold(threshold: PreparedThreshold): PreparedThreshold {
  return { ...threshold, spent: true };
}

/**
 * Complete one stored body into a station. This is the slice's escalation path
 * to 3/3/3 and runs only at an authored work/escalation milestone — never on a
 * hidden countdown. Capacity and the 30-metre tether still bind.
 */
export function completeBody(
  continuity: Continuity,
  operation: RemainsOperation,
  patterns: readonly WorkPattern[],
  input: { stationId: string; remainsId: string; workPatternId: string; escalationEventId: string },
): { continuity: Continuity; operation: RemainsOperation } {
  if (!input.escalationEventId) throw new Error('Completing a body needs an authored escalation milestone');
  const pattern = patterns.find(candidate => candidate.id === input.workPatternId);
  if (!pattern) throw new Error(`Unknown work pattern ${input.workPatternId}`);
  const station = operation.stations.find(candidate => candidate.id === input.stationId);
  if (!station) throw new Error(`Unknown station ${input.stationId}`);
  if (station.integrity !== 'intact') throw new Error(`${station.id} has no working anchor; a stilled station stays stilled`);
  if (station.bodies.length >= MAX_BODIES_PER_STATION) throw new Error(`${station.id} already binds three bodies`);
  const stored = operation.storage.find(body => body.remainsId === input.remainsId);
  if (!stored) throw new Error(`${input.remainsId} is not an incomplete body in storage`);
  for (const step of pattern.steps) {
    if (!withinTether(station.anchor, step)) throw new Error(`Pattern ${pattern.id} would drag ${input.remainsId} beyond 30 metres of its anchor`);
  }
  const nextContinuity = changeRemains(continuity, input.remainsId, 'animated');
  const nextOperation = structuredClone(operation);
  const nextStation = nextOperation.stations.find(candidate => candidate.id === input.stationId)!;
  // Completion preparation installs the body at the station; it starts on its pattern.
  nextStation.bodies.push({ remainsId: input.remainsId, position: structuredClone(pattern.steps[0]!), workPatternId: pattern.id, patternStep: 0, status: 'active' });
  nextStation.bodies.sort((a, b) => compareText(a.remainsId, b.remainsId));
  nextOperation.storage = nextOperation.storage.filter(body => body.remainsId !== input.remainsId);
  return { continuity: nextContinuity, operation: nextOperation };
}

/**
 * Break an anchor by ordinary force, or burn the station. Both end the imposed
 * motion; neither touches the dead person's identity. Fire also destroys the
 * remains and the evidence they carry. Stilled bodies cannot be re-bound here:
 * the correspondence is gone with the anchor.
 */
export function breakAnchor(
  continuity: Continuity,
  operation: RemainsOperation,
  stationId: string,
  method: 'force' | 'fire',
): { continuity: Continuity; operation: RemainsOperation } {
  const station = operation.stations.find(candidate => candidate.id === stationId);
  if (!station) throw new Error(`Unknown station ${stationId}`);
  if (station.integrity !== 'intact') throw new Error(`${stationId}'s anchor is already ${station.integrity}`);
  const nextOperation = structuredClone(operation);
  const nextStation = nextOperation.stations.find(candidate => candidate.id === stationId)!;
  nextStation.integrity = 'broken';
  for (const body of nextStation.bodies) stillBody(nextOperation, nextStation, body, 'anchor-broken');
  let nextContinuity = continuity;
  for (const body of station.bodies) {
    nextContinuity = changeRemains(nextContinuity, body.remainsId, method === 'fire' ? 'destroyed' : 'exposed');
  }
  return { continuity: nextContinuity, operation: nextOperation };
}

/**
 * The Severing Mark's careful release: deactivate one station while preserving
 * identifiable remains and the tether evidence. Requires proof of the actual
 * tether arrangement — it is not a combat-wide dispel.
 */
export function releaseAnchor(
  continuity: Continuity,
  operation: RemainsOperation,
  stationId: string,
  evidence: readonly string[],
): { continuity: Continuity; operation: RemainsOperation } {
  const station = operation.stations.find(candidate => candidate.id === stationId);
  if (!station) throw new Error(`Unknown station ${stationId}`);
  if (station.integrity !== 'intact') throw new Error(`${stationId}'s anchor is already ${station.integrity}`);
  const required = tetherEvidenceId(stationId);
  if (!evidence.includes(required)) throw new Error(`The Severing Mark needs evidence of the actual tether arrangement (${required})`);
  const nextOperation = structuredClone(operation);
  const nextStation = nextOperation.stations.find(candidate => candidate.id === stationId)!;
  nextStation.integrity = 'released';
  for (const body of nextStation.bodies) stillBody(nextOperation, nextStation, body, 'anchor-released');
  let nextContinuity = continuity;
  for (const body of station.bodies) nextContinuity = changeRemains(nextContinuity, body.remainsId, 'exposed');
  return { continuity: nextContinuity, operation: nextOperation };
}

export const tetherEvidenceId = (stationId: string): string => `tether-arrangement:${stationId}`;
export const workingRemainsEvidenceId = (remainsId: string): string => `working-remains:${remainsId}`;

/** Close inspection of an intact station reveals its tether arrangement. The observer must get within 30 metres of the anchor. */
export function inspectStation(operation: RemainsOperation, stationId: string, observer: NavPosition): EvidenceFact {
  const station = operation.stations.find(candidate => candidate.id === stationId);
  if (!station) throw new Error(`Unknown station ${stationId}`);
  if (station.integrity !== 'intact') throw new Error(`${stationId}'s tether arrangement is gone with its anchor`);
  if (!withinTether(station.anchor, observer)) throw new Error('The observer must get close enough to read the anchor');
  return { id: tetherEvidenceId(stationId), kind: 'necromancy-tether', finding: `The anchor arrangement binding ${station.bodies.length} bodies at ${stationId}`, source: 'docs/world/08-magic.md: A station\'s limits' };
}

/** Watching an active body work is unmistakable evidence of necromancy, naming the dead as a worker — not a monster. */
export function observeWorkingRemains(continuity: Continuity, operation: RemainsOperation): EvidenceFact[] {
  validateLedger(continuity);
  const facts: EvidenceFact[] = [];
  for (const station of operation.stations) {
    for (const body of station.bodies) {
      if (body.status !== 'active') continue;
      const remains = continuity.remains[body.remainsId]!;
      const name = continuity.persons[remains.personId]?.current.name ?? remains.personId;
      facts.push({ id: workingRemainsEvidenceId(body.remainsId), kind: 'necromancy-working', finding: `${name}'s body repeats imposed work at ${station.id}; the person is dead and this motion is not their mind`, source: 'docs/world/08-magic.md: The bodily remainder' });
    }
  }
  return facts;
}
